import React, { useState, useEffect } from 'react';
import { getCollection, deleteDocument, updateDocument } from '../services/resilientDb';
import './JobRequestsPage.css';

const JobRequestsPage = () => {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');

    useEffect(() => {
        fetchJobs();
    }, []);

    const fetchJobs = async () => {
        try {
            // Resilient fetch: tries Firebase first, falls back to Supabase
            const jobsList = await getCollection('bookings', {
                orderByField: 'createdAt',
                orderDirection: 'desc',
            });
            setJobs(jobsList);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching jobs:', error);
            setLoading(false);
        }
    };

    const handleDelete = async (jobId) => {
        if (window.confirm('Are you sure you want to delete this job request?')) {
            try {
                await deleteDocument('bookings', jobId);
                setJobs(jobs.filter(job => job.id !== jobId));
                alert('Job request deleted successfully');
            } catch (error) {
                console.error('Error deleting job:', error);
                alert('Failed to delete job request');
            }
        }
    };

    const handleStatusChange = async (jobId, newStatus) => {
        try {
            await updateDocument('bookings', jobId, { status: newStatus });
            setJobs(jobs.map(job =>
                job.id === jobId ? { ...job, status: newStatus } : job
            ));
            alert('Status updated successfully');
        } catch (error) {
            console.error('Error updating status:', error);
            alert('Failed to update status');
        }
    };

    const filteredJobs = filter === 'all'
        ? jobs
        : jobs.filter(job => job.status === filter);

    if (loading) {
        return (
            <div className="job-requests-page">
                <div className="container">
                    <div className="loading">Loading job requests...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="job-requests-page">
            <div className="container">
                <div className="page-header">
                    <div>
                        <h1>Job Requests</h1>
                        <p>Manage all service requests from customers</p>
                    </div>
                    <div className="filter-buttons">
                        <button
                            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
                            onClick={() => setFilter('all')}
                        >
                            All ({jobs.length})
                        </button>
                        <button
                            className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
                            onClick={() => setFilter('pending')}
                        >
                            Pending ({jobs.filter(j => j.status === 'pending').length})
                        </button>
                        <button
                            className={`filter-btn ${filter === 'accepted' ? 'active' : ''}`}
                            onClick={() => setFilter('accepted')}
                        >
                            Accepted ({jobs.filter(j => j.status === 'accepted').length})
                        </button>
                        <button
                            className={`filter-btn ${filter === 'completed' ? 'active' : ''}`}
                            onClick={() => setFilter('completed')}
                        >
                            Completed ({jobs.filter(j => j.status === 'completed').length})
                        </button>
                    </div>
                </div>

                {filteredJobs.length === 0 ? (
                    <div className="empty-state">
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="12" />
                            <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                        <h3>No job requests found</h3>
                        <p>There are no {filter !== 'all' ? filter : ''} job requests at the moment.</p>
                    </div>
                ) : (
                    <div className="jobs-grid">
                        {filteredJobs.map((job) => (
                            <div key={job.id} className="job-card">
                                <div className="job-card-header">
                                    <h3>{job.serviceType || 'Service Request'}</h3>
                                    <div className={`status-badge status-${job.status || 'pending'}`}>
                                        {job.status || 'pending'}
                                    </div>
                                </div>

                                <div className="job-card-body">
                                    <div className="job-info-row">
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                            <circle cx="12" cy="7" r="4" />
                                        </svg>
                                        <span>{job.customerName || job.name || 'Unknown'}</span>
                                    </div>

                                    <div className="job-info-row">
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                            <circle cx="12" cy="10" r="3" />
                                        </svg>
                                        <span>{job.location || job.address || 'No location'}</span>
                                    </div>

                                    <div className="job-info-row">
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                            <line x1="16" y1="2" x2="16" y2="6" />
                                            <line x1="8" y1="2" x2="8" y2="6" />
                                            <line x1="3" y1="10" x2="21" y2="10" />
                                        </svg>
                                        <span>{job.date || new Date(job.createdAt).toLocaleDateString()}</span>
                                    </div>

                                    {job.description && (
                                        <p className="job-description">{job.description}</p>
                                    )}

                                    {job.estimatedPrice && (
                                        <div className="job-price">₹{job.estimatedPrice}</div>
                                    )}
                                </div>

                                <div className="job-card-actions">
                                    <select
                                        value={job.status || 'pending'}
                                        onChange={(e) => handleStatusChange(job.id, e.target.value)}
                                        className="status-select"
                                    >
                                        <option value="pending">Pending</option>
                                        <option value="accepted">Accepted</option>
                                        <option value="completed">Completed</option>
                                        <option value="rejected">Rejected</option>
                                    </select>
                                    <button
                                        onClick={() => handleDelete(job.id)}
                                        className="delete-btn"
                                    >
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                            <polyline points="3 6 5 6 21 6" />
                                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                        </svg>
                                        Delete
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default JobRequestsPage;
